import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import utils from "../../universal/utils";
import { canSubmitDeletionRequest } from "./accountDataDeletionUtils";
import AccountDeletitionDialog from "./AccountDeletitionDialog";
import {
  ACCOUNT_PAGE_DELETION_BUTTON_SX,
  ACCOUNT_PAGE_DELETION_BUTTON_STACKED_SX,
  ACCOUNT_PAGE_DELETION_CONTENT_SX,
  ACCOUNT_PAGE_DELETION_COPY_SX,
  ACCOUNT_PAGE_DELETION_LINK_SX,
  ACCOUNT_PAGE_DELETION_ROW_SX,
  ACCOUNT_PAGE_DELETION_SX,
  ACCOUNT_PAGE_DELETION_TITLE_SX,
  ACCOUNT_PAGE_FIELD_ERROR_SX,
  ACCOUNT_PAGE_FIELD_LABEL_SX,
} from "./accountPageStyles";

const basePath = utils.getEdgeBasePath();
const deletionHeadingId = "account-data-deletion-heading";
const deletionToggleId = "account-data-deletion-toggle";
const deletionRegionId = "account-data-deletion-region";

const DELETION_DESCRIPTION =
  "Permanently delete your account and personal data. Once confirmed, this action is final and your account cannot be recovered.";

export default function AccountDataDeletionSection({
  user,
  defaultExpanded = false,
  embedded = false,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded || embedded);
  const [form, setForm] = useState({
    confirmationText: "",
    twoFactorCode: "",
  });

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${basePath}/api/v1/data-deletion-request`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          confirmationText: form.confirmationText,
          twoFactorCode: form.twoFactorCode.trim(),
        }),
      });
      const data = await response.json();
      if (!response.ok || !data?.deleted) {
        throw new Error(data?.message || "Failed to delete account");
      }

      setDialogOpen(false);
      setForm({ confirmationText: "", twoFactorCode: "" });
      window.setTimeout(() => {
        window.location.assign("/");
      }, 800);
    } catch (submitError) {
      setError(submitError.message || "Failed to delete account");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = canSubmitDeletionRequest({
    confirmationText: form.confirmationText,
    twoFactorEnabled: !!user?.twoFactorEnabled,
    twoFactorCode: form.twoFactorCode,
  });
  const showContent = embedded || expanded;

  const deleteButton = (
    <Box
      component="button"
      type="button"
      onClick={() => setDialogOpen(true)}
      aria-label="Delete account"
      sx={embedded ? ACCOUNT_PAGE_DELETION_BUTTON_SX : ACCOUNT_PAGE_DELETION_BUTTON_STACKED_SX}
    >
      Delete
    </Box>
  );

  return (
    <Box sx={embedded ? ACCOUNT_PAGE_DELETION_SX : { ...ACCOUNT_PAGE_DELETION_SX, mt: 4 }}>
      {embedded ? (
        <Box
          id={deletionRegionId}
          role="region"
          aria-labelledby={deletionHeadingId}
          sx={ACCOUNT_PAGE_DELETION_SX}
        >
          <Box sx={ACCOUNT_PAGE_DELETION_ROW_SX}>
            <Box sx={ACCOUNT_PAGE_DELETION_CONTENT_SX}>
              <Typography
                id={deletionHeadingId}
                component="h3"
                sx={ACCOUNT_PAGE_DELETION_TITLE_SX}
              >
                Delete Account
              </Typography>
              <Typography sx={ACCOUNT_PAGE_DELETION_COPY_SX}>{DELETION_DESCRIPTION}</Typography>
            </Box>
            {deleteButton}
          </Box>

          {error && !dialogOpen ? (
            <Typography sx={{ ...ACCOUNT_PAGE_FIELD_ERROR_SX, mt: "0.85rem" }}>
              {error}
            </Typography>
          ) : null}

          <Typography
            component={Link}
            to="/data-protection-policy-and-gdpr"
            sx={ACCOUNT_PAGE_DELETION_LINK_SX}
          >
            Review our GDPR policy
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
              mb: showContent ? "0.85rem" : 0,
            }}
          >
            <Typography id={deletionHeadingId} component="h3" sx={ACCOUNT_PAGE_FIELD_LABEL_SX}>
              Delete Account
            </Typography>
            <Box
              component="button"
              type="button"
              id={deletionToggleId}
              aria-expanded={expanded}
              aria-controls={deletionRegionId}
              onClick={() => setExpanded((prev) => !prev)}
              sx={ACCOUNT_PAGE_DELETION_BUTTON_SX}
            >
              {expanded ? "Hide details" : "Show details"}
            </Box>
          </Box>

          <Box
            id={deletionRegionId}
            role="region"
            aria-labelledby={deletionHeadingId}
            hidden={!showContent}
            sx={ACCOUNT_PAGE_DELETION_SX}
          >
            {showContent ? (
              <>
                <Typography sx={ACCOUNT_PAGE_DELETION_COPY_SX}>
                  {DELETION_DESCRIPTION}
                </Typography>

                {error && !dialogOpen ? (
                  <Typography sx={{ ...ACCOUNT_PAGE_FIELD_ERROR_SX, mt: "0.85rem" }}>
                    {error}
                  </Typography>
                ) : null}

                <Typography
                  component={Link}
                  to="/data-protection-policy-and-gdpr"
                  sx={ACCOUNT_PAGE_DELETION_LINK_SX}
                >
                  Review our GDPR policy
                </Typography>

                {deleteButton}
              </>
            ) : null}
          </Box>
        </>
      )}

      <AccountDeletitionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
        form={form}
        onFormChange={handleFormChange}
        twoFactorEnabled={Boolean(user?.twoFactorEnabled)}
        error={dialogOpen ? error : null}
        canSubmit={canSubmit}
      />
    </Box>
  );
}

AccountDataDeletionSection.propTypes = {
  user: PropTypes.object,
  defaultExpanded: PropTypes.bool,
  embedded: PropTypes.bool,
};